/**
 * A generated image in the conversation (web), the way Gemini draws one: a
 * block in the response, not a card. Never cropped: the frame takes the
 * image's own ratio (the stored size, else the requested ratio, else square),
 * spans the response column (708px at most) and rounds its corners at 16px.
 * While the image is made or loads, the frame holds Gemini's moving
 * three-step gradient; when it arrives the gradient fades out over 0.2s and
 * the image scales in from 0 over 0.2s (both off under reduced motion).
 *
 * Pointer devices get two round 32px controls on hover or keyboard focus:
 * Download full size and More (Copy link, Open). Touch gets none: a tap
 * opens the lightbox, a long press opens a bottom sheet (Save image, Copy
 * link, Share). `GeneratedImage.tsx` is the native twin.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { SpinnerGlyph } from '../common/SpinnerGlyph';
import { WebMenu, WebMenuItem } from '../web/WebMenu';
import { useToast } from '../../hooks/useToast';
import { useArtifactSignedUrl } from '../../hooks/useArtifact';
import { downloadArtifactFile } from '../../lib/artifact-download';
import { artifactShareUrl, copyArtifactLink } from '../../lib/artifact-links';
import { ImageLightbox } from './ImageLightbox';
import {
  imageAspectRatio,
  imageDisplayVariant,
  type GeneratedImageProps,
} from './artifact-model';
import { useImageEditController } from './editor/useImageEditController';

const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP = 10;
const PHONE_QUERY = '(max-width: 767px)';

export function GeneratedImage({ model, tileWidth }: GeneratedImageProps) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const variant = imageDisplayVariant(model);
  // An edit the agent started (`editImage`): pending until its version is the head.
  const edit = useImageEditController(model.resultVersionId ? model.artifactId : null, {
    followVersionId: model.resultVersionId ?? null,
    enabled: Boolean(model.resultVersionId),
  });
  // A new version (an edit) mints a new link, so the image shows it: an
  // edit's card follows the head it reads, so the new image replaces the
  // old one when the edit settles, without a refresh.
  const followedHead = model.resultVersionId ? edit.detail.data?.currentVersion?.id ?? null : null;
  const signed = useArtifactSignedUrl(model.artifactId || null, variant, variant !== null, 'light', {
    revision: followedHead ?? (model.versionNumber ? String(model.versionNumber) : null),
  });
  // The call is still running (no result yet), or its version is being made.
  const editing = Boolean(model.editing) || edit.running;
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const openRef = useRef<HTMLButtonElement | null>(null);
  const moreRef = useRef<HTMLButtonElement | null>(null);
  const press = useRef<{ timer: number | null; x: number; y: number; fired: boolean }>({
    timer: null,
    x: 0,
    y: 0,
    fired: false,
  });

  useEffect(
    () => () => {
      if (press.current.timer !== null) window.clearTimeout(press.current.timer);
    },
    [],
  );

  const title = model.title || t('chat.artifacts.kind.image');
  const url = signed.url && !signed.expired ? signed.url : null;
  const loaded = Boolean(url) && loadedUrl === url;
  const ratio = imageAspectRatio(model);

  const download = async () => {
    if (busy || !model.artifactId) return;
    setBusy(true);
    try {
      await downloadArtifactFile({ artifactId: model.artifactId, variant: 'image', title });
    } catch {
      toast.error(t('chat.artifacts.download.failed'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (await copyArtifactLink(model.artifactId)) toast.success(t('chat.artifacts.actions.linkCopied'));
    else toast.error(t('chat.artifacts.actions.copyFailed'));
  };

  const share = async () => {
    const link = artifactShareUrl(model.artifactId);
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav?.share) {
      try {
        await nav.share({ title, url: link });
        return;
      } catch {
        // Dismissed or refused: fall back to copying the link.
      }
    }
    await copyLink();
  };

  const clearPress = () => {
    if (press.current.timer !== null) window.clearTimeout(press.current.timer);
    press.current.timer = null;
  };

  const closeLightbox = () => {
    setLightbox(false);
    openRef.current?.focus({ preventScroll: true });
  };

  const sheetStyle =
    typeof window !== 'undefined' && window.matchMedia?.(PHONE_QUERY).matches
      ? { borderRadius: '28px 28px 0 0', paddingTop: 16, paddingBottom: 56 }
      : { borderRadius: 12 };

  return (
    <div
      className="tabbio-image-block"
      style={tileWidth ? { width: tileWidth, maxWidth: tileWidth } : undefined}
    >
      <div
        className={`tabbio-image-frame${lightbox ? ' is-expanded' : ''}`}
        style={{ aspectRatio: String(ratio) }}
      >
        <div className={`tabbio-image-loader${loaded ? ' is-done' : ''}`} aria-hidden />
        {editing && loaded ? (
          <span className="tabbio-image-edit-overlay" role="status">
            <SpinnerGlyph color="#ffffff" size={20} />
            {t('chat.artifacts.image.editing')}
          </span>
        ) : null}
        {url ? (
          <button
            ref={openRef}
            type="button"
            className="tabbio-image-open"
            aria-label={t('chat.artifacts.openImage', { title })}
            onClick={(event) => {
              if (press.current.fired) {
                press.current.fired = false;
                event.preventDefault();
                return;
              }
              setLightbox(true);
            }}
            onPointerDown={(event) => {
              if (event.pointerType !== 'touch') return;
              press.current = { timer: null, x: event.clientX, y: event.clientY, fired: false };
              press.current.timer = window.setTimeout(() => {
                press.current.fired = true;
                press.current.timer = null;
                setSheetOpen(true);
              }, LONG_PRESS_MS);
            }}
            onPointerMove={(event) => {
              if (press.current.timer === null) return;
              if (
                Math.abs(event.clientX - press.current.x) > LONG_PRESS_SLOP ||
                Math.abs(event.clientY - press.current.y) > LONG_PRESS_SLOP
              ) {
                clearPress();
              }
            }}
            onPointerUp={clearPress}
            onPointerCancel={clearPress}
            onContextMenu={(event) => {
              // A touch long press is ours; the mouse keeps its native menu.
              if (press.current.fired || press.current.timer !== null) event.preventDefault();
            }}
          >
            <img
              key={url}
              src={url}
              alt={title}
              className={`tabbio-image${loaded ? ' is-loaded' : ''}`}
              referrerPolicy="no-referrer"
              draggable={false}
              onLoad={() => setLoadedUrl(url)}
              onError={signed.reportLoadError}
            />
          </button>
        ) : null}

        {loaded ? (
          <div className="tabbio-image-controls">
            <button
              type="button"
              className="tabbio-image-control"
              onClick={() => void download()}
              aria-label={t('chat.artifacts.actions.downloadFullSize')}
              title={t('chat.artifacts.actions.downloadFullSize')}
              aria-busy={busy || undefined}
            >
              {busy ? (
                <SpinnerGlyph color="var(--text-secondary)" size={18} />
              ) : (
                <MaterialSymbol name="download" size={20} />
              )}
            </button>
            <button
              ref={moreRef}
              type="button"
              className="tabbio-image-control"
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t('chat.artifacts.actions.more')}
              title={t('chat.artifacts.actions.more')}
            >
              <MaterialSymbol name="more_vert" size={20} />
            </button>
          </div>
        ) : null}
      </div>
      {edit.failure ? (
        <div className="tabbio-artifact-note" role="status">
          <span dir="auto">{edit.failure.message || t('chat.artifacts.image.editFailed')}</span>
        </div>
      ) : null}

      <WebMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchor={moreRef.current}
        placement="bottom-start"
        minWidth={200}
        style={{ borderRadius: 12 }}
      >
        <WebMenuItem icon="link" label={t('chat.artifacts.actions.copyLink')} onSelect={() => void copyLink()} />
        <WebMenuItem icon="open_in_full" label={t('chat.artifacts.actions.open')} onSelect={() => setLightbox(true)} />
      </WebMenu>

      <WebMenu
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        anchor={openRef.current}
        placement="bottom-start"
        minWidth={240}
        mobileSheet
        style={sheetStyle}
      >
        <WebMenuItem icon="download" label={t('chat.artifacts.actions.saveImage')} onSelect={() => void download()} />
        <WebMenuItem icon="link" label={t('chat.artifacts.actions.copyLink')} onSelect={() => void copyLink()} />
        <WebMenuItem icon="share" label={t('chat.artifacts.actions.share')} onSelect={() => void share()} />
      </WebMenu>

      {lightbox && model.artifactId ? (
        <ImageLightbox artifactId={model.artifactId} title={title} onClose={closeLightbox} />
      ) : null}
    </div>
  );
}
